import React, { useEffect, useState } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { getMessagingInstance } from '../../config/firebase';
import { toast } from 'react-toastify';
import { useUser } from '@auth0/nextjs-auth0/client';

const PushNotifications = () => {
  const { user } = useUser();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [messaging, setMessaging] = useState(null);

  useEffect(() => {
    // Verificar si las notificaciones están soportadas
    const checkSupport = async () => {
      if ('Notification' in window && 'serviceWorker' in navigator) {
        setIsSupported(true);
        setPermission(Notification.permission);
        
        // Obtener instancia de messaging
        const messagingInstance = await getMessagingInstance();
        setMessaging(messagingInstance);
        
        // Si ya tenemos permisos, inicializar
        if (Notification.permission === 'granted' && messagingInstance) {
          await initializeMessaging(messagingInstance);
        }
      }
    };

    checkSupport();
  }, []);

  useEffect(() => {
    if (!messaging || !user) return;

    // Escuchar mensajes cuando la app está en primer plano
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Mensaje recibido en primer plano:', payload);
      
      // Mostrar notificación con toast
      const { title, body, icon } = payload.notification || {};
      
      toast.info(
        <div className="flex items-center space-x-3">
          {icon && <img src={icon} alt="" className="w-10 h-10 rounded" />}
          <div>
            <div className="font-semibold">{title}</div>
            <div className="text-sm">{body}</div>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // También mostrar notificación nativa si está permitido
      if (Notification.permission === 'granted') {
        new Notification(title || 'PLAVED', {
          body: body || 'Nueva notificación',
          icon: icon || '/icons/android-icon-192x192.png',
          badge: '/icons/android-icon-96x96.png',
          vibrate: [100, 50, 100],
          data: payload.data || {},
        });
      }
    });

    return () => unsubscribe();
  }, [messaging, user]);

  const initializeMessaging = async (messagingInstance) => {
    const msg = messagingInstance || messaging;
    if (!msg) {
      console.log('Messaging no está disponible');
      return;
    }

    try {
      // Obtener el token FCM
      const currentToken = await getToken(msg, {
        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY
      });

      if (currentToken) {
        console.log('Token FCM obtenido:', currentToken);
        
        // Enviar el token al backend para almacenarlo
        if (user) {
          await saveTokenToBackend(currentToken);
        }
      } else {
        console.log('No se pudo obtener el token FCM');
      }
    } catch (error) {
      console.error('Error al obtener el token FCM:', error);
    }
  };

  const saveTokenToBackend = async (token) => {
    try {
      // Guardar el token en Firebase Realtime Database
      const { database } = await import('../../config/firebase');
      const { ref, set } = await import('firebase/database');
      
      const tokenRef = ref(database, `fcm_tokens/${user.sub}`);
      await set(tokenRef, {
        token,
        userEmail: user.email,
        platform: getPlatform(),
        lastUpdated: new Date().toISOString()
      });
      
      console.log('Token FCM guardado en Firebase Database');
    } catch (error) {
      console.error('Error al guardar el token FCM:', error);
    }
  };

  const getPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/android/.test(userAgent)) return 'android';
    if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
    return 'web';
  };

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      
      if (permission === 'granted') {
        console.log('Permisos de notificación concedidos');
        await initializeMessaging(messaging);
        toast.success('¡Notificaciones activadas! Recibirás actualizaciones importantes.');
      } else if (permission === 'denied') {
        toast.error('Notificaciones bloqueadas. Puedes habilitarlas en la configuración del navegador.');
      }
    } catch (error) {
      console.error('Error al solicitar permisos:', error);
      toast.error('Error al habilitar notificaciones');
    }
  };

  // No mostrar nada si no está soportado o si ya tiene permisos
  if (!isSupported || permission === 'granted' || !user) {
    return null;
  }

  // Mostrar banner para solicitar permisos
  if (permission === 'default') {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:max-w-md bg-white shadow-lg rounded-lg p-4 z-40 border border-gray-200">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              Mantente informado
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Recibe notificaciones sobre actualizaciones importantes en tus proyectos.
            </p>
            <div className="mt-3 flex space-x-3">
              <button
                onClick={requestPermission}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Activar
              </button>
              <button
                onClick={() => setPermission('denied')}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PushNotifications;